import type { ReactNode } from "react";
import { createPortal } from "react-dom";

/// Palier d'empilement **commun** à tous les calques plein écran (`Shutter`, `Modal`,
/// `ConfirmModal`).
///
/// Une valeur unique, et non une échelle par type de calque : les calques sont montés sous `<body>`
/// par [OverlayPortal] dans leur **ordre d'ouverture**, et deux éléments positionnés de même
/// `z-index` se peignent dans l'ordre du document — le dernier ouvert passe donc au-dessus, tout
/// seul.
///
/// C'est la seule règle qui tienne, parce que l'imbrication n'est connue qu'à l'exécution : une
/// question s'ouvre en `Modal` **depuis** le volet de partie, son historique en `Shutter`
/// **depuis** cette modale, et la confirmation de restauration **depuis** cet historique. Aucune
/// table de z-index maintenue à la main ne peut ordonner ces quatre couches, dont la profondeur
/// dépend du chemin emprunté par l'utilisateur.
///
/// 100 place l'ensemble au-dessus de tout le contenu applicatif (le plus haut y est `z-50`, la
/// barre latérale).
export const OVERLAY_Z = "z-[100]";

/// Monte un calque plein écran directement sous `<body>`.
///
/// Un élément `position: fixed` se cale sur le **viewport**, sauf si un ancêtre porte une
/// `transform` : cet ancêtre devient alors son bloc conteneur. Or le volet [Shutter] s'ouvre en
/// `translateX`, et son `animation-fill-mode: both` laisse la transform appliquée après
/// l'animation — indéfiniment. Tout calque rendu à l'intérieur d'un volet (voile, modale,
/// confirmation) se retrouvait donc confiné à la boîte du volet : voile absent sur le reste de la
/// page, et boîte de confirmation à la largeur du volet parent au lieu de la page.
///
/// La propagation des événements React traverse le portail : un `onClick` d'ancêtre continue de
/// recevoir les clics du calque, comme avant.
export function OverlayPortal({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(children, document.body);
}
